import { useId, useState } from 'react'
import { linkSummaryNote, type LinkSummary } from '../links/linkSummary'
import { CopyIconButton } from './CopyIconButton'
import { Icon } from './Icon'

interface GistLineProps {
  summary: LinkSummary
  onRefresh: () => void
  onHide: () => void
  /** Opens the full-screen view of this generation. */
  onExpand?: () => void
}

/**
 * What a link says about its page: the generated gist (marked, with the reasoning behind an info mark) or, until
 * there is one, the site's own description. A read the person asked for always answers: it shows that it is reading,
 * and says why when nothing came back. Nothing shows while there is no text and nothing was asked.
 */
export function GistLine({ summary, onRefresh, onHide, onExpand }: GistLineProps) {
  const [explaining, setExplaining] = useState(false)
  const panelId = useId()
  const reading = summary.state === 'reading'
  const note = linkSummaryNote(summary)

  if (!summary.text) {
    if (reading) {
      return <div className="gist-line gist-line--reading" role="status" aria-label="Reading the page">
        <Icon name="spark" /><span className="gist-line__bars" aria-hidden="true"><i /><i /></span>
      </div>
    }
    if (!note) return null
    return <div className="gist-line gist-line--note" role="status">
      <div className="gist-line__row"><Icon name="help" /><p>{note}</p></div>
      {summary.state === 'failed' ? <button type="button" className="btn btn-ghost gist-line__retry" onClick={onRefresh}>Try again</button> : null}
    </div>
  }

  const generated = summary.source === 'gist'
  return <div className={generated ? 'gist-line' : 'gist-line gist-line--site'} aria-busy={reading || undefined}>
    <div className="gist-line__row" data-reading={reading || undefined}>
      {generated ? <Icon name="spark" /> : null}
      <p>{summary.text}</p>
    </div>
    {generated ? <div className="gist-line__tools">
      <button type="button" className="btn btn-ghost btn-square gist-line__info" aria-label="Where this summary comes from"
        aria-expanded={explaining} aria-controls={explaining ? panelId : undefined} onClick={() => setExplaining((open) => !open)}>
        <Icon name="help" />
      </button>
      <CopyIconButton text={summary.text} label="Copy summary" />
      {onExpand ? <button type="button" className="btn btn-ghost btn-square" aria-label="Open full screen" title="Open full screen" onClick={onExpand}><Icon name="expand" /></button> : null}
    </div> : null}
    {note && !reading ? <p className="gist-line__note" role="status">{note}</p> : null}
    {explaining ? <div className="gist-line__panel" id={panelId}>
      <p><strong>Written by miniMany from the page text.</strong> It is not part of your note and is not exported with it.</p>
      <div className="gist-line__actions">
        <button type="button" className="btn" onClick={() => { setExplaining(false); onRefresh() }}>Refresh</button>
        <button type="button" className="btn" onClick={() => { setExplaining(false); onHide() }}>Hide for this link</button>
      </div>
    </div> : null}
  </div>
}
